import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const [panelWidth, setPanelWidth] = useState('auto');

    useEffect(() => {
        const dropdownEl = dropdownRef.current?.getElement();
        if (!dropdownEl) return;

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setPanelWidth(`${entry.contentRect.width}px`);
            }
        });

        observer.observe(dropdownEl);

        return () => {
            observer.disconnect();
        };
    }, []);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);

                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 10);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label"
            placeholder={placeholder}
            className={className || "form-input"}
            panelClassName={`custom-wrap-dropdown ${panelUniqueClass}`}
            panelStyle={{ width: panelWidth, maxWidth: panelWidth }}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
        />
    );
};

const JournalForm = ({ isOpen, onClose, onSubmit, formData, setFormData, isEditing, countries = [], journalTypes = [], toast, zIndex = 1000 }) => {

    const countryOptions = useMemo(() => {
        return countries.map(c => ({
            label: c.Name,
            value: c.Name
        }));
    }, [countries]);

    const journalTypeOptions = useMemo(() => {
        return journalTypes.map(jt => ({
            label: jt.Name,
            value: jt.Id
        }));
    }, [journalTypes]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'JournalTypeId') {
            const selectedType = journalTypes.find(jt => jt.Id.toString() === value.toString());

            setFormData({
                ...formData,
                JournalTypeId: value,
                Score: selectedType ? selectedType.Score : ''
            });
        } else {
            setFormData({
                ...formData,
                [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : Number(value)) : value)
            });
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex }}>
            <div className="modal-box" style={{ width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '20px', lineHeight: '1.4' }}>
                        {isEditing ? "Cập nhật Tạp chí" : "Thêm mới Tạp chí"}
                    </h3>
                    <button type="button" className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    <form id="journalForm" onSubmit={onSubmit}>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Tên tạp chí *</label>
                            <input
                                type="text"
                                name="Name"
                                className="form-input"
                                value={formData.Name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Loại tạp chí *</label>
                            <SearchableFocusDropdown
                                value={formData.JournalTypeId}
                                options={journalTypeOptions}
                                onChange={(e) => handleChange({ target: { name: 'JournalTypeId', value: e.value } })}
                                placeholder="Chọn loại tạp chí"
                                panelUniqueClass="unique-journal-type-panel"
                            />
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label>ISSN/ISBN</label>
                                <input type="text" name="ISSN" className="form-input" value={formData.ISSN} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Điểm công trình *</label>
                                <input type="number" step="0.1" name="Score" className="form-input" value={formData.Score} onChange={handleChange} required />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px' }}>Quốc gia</label>
                                <SearchableFocusDropdown
                                    value={formData.Country}
                                    options={countryOptions}
                                    onChange={(e) => handleChange({ target: { name: 'Country', value: e.value } })}
                                    placeholder="Chọn quốc gia"
                                    panelUniqueClass="unique-country-panel"
                                />
                            </div>

                            <div className="form-group">
                                <label>Cơ quan xuất bản</label>
                                <input type="text" name="Publisher" className="form-input" value={formData.Publisher} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Impact Factor</label>
                                <input type="text" name="ImpactFactor" className="form-input" value={formData.ImpactFactor} onChange={handleChange} />
                            </div>

                            <div className="form-group">
                                <label>Q-Ranking</label>
                                <select name="QCategory" className="form-input" value={formData.QCategory} onChange={handleChange}>
                                    <option value="">Chọn Q</option>
                                    {["Q1", "Q2", "Q3", "Q4", "Không có Q"].map(q => <option key={q} value={q}>{q}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', paddingBottom: '20px' }}>
                            <input type="checkbox" name="IsHDCGS" checked={formData.IsHDCGS} onChange={handleChange} style={{ width: '18px', height: '18px', cursor: 'pointer' }} id="hdcgs" />
                            <label htmlFor="hdcgs" style={{ margin: 0, cursor: 'pointer', fontWeight: '500', color: '#2c3e50' }}>Thuộc hội đồng chức danh giáo sư nhà nước</label>
                        </div>

                    </form>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn-cancel" onClick={onClose}>Hủy</button>
                    <button type="submit" form="journalForm" className="btn-submit">Hoàn tất</button>
                </div>
            </div>
        </div>
    );
};

export default JournalForm;